import { normalizeChartingCollection } from '@/features/charting-next/adapters/chartingCollectionAdapters';
import { buildChartingDescriptionSearchPayload } from '@/features/charting-next/adapters/chartingPayloads';
import { get, postForm } from '@/features/charting-next/api/chartingRepositoryClient';

export async function fetchDefaultStocks(entry = {}) {
    const payload = await get('/api/v1/stock-finder/stocks');

    return normalizeChartingCollection(payload, entry);
}

export async function fetchNamedFinderStocks(entry = {}, finderName = '') {
    const payload = await get(`/api/v1/stock-finder/stocks/finder/${encodeURIComponent(String(finderName || ''))}`);

    return normalizeChartingCollection(payload, entry);
}

export async function fetchCompatibilityStocks(entry = {}, { currency = '' } = {}) {
    const payload = await get('/api/v1/stock-finder/stocks/compatibility', {
        params: {
            ...(currency ? { currency } : {})
        }
    });

    return normalizeChartingCollection(payload, entry);
}

export async function fetchLastIpoStocks(entry = {}) {
    const payload = await get('/api/v1/stock-finder/last-ipos');

    return normalizeChartingCollection(payload, entry);
}

export async function fetchBestBullishStocks(entry = {}, period = '') {
    const payload = await get(`/api/v1/stock-finder/best-bullish-values/${encodeURIComponent(String(period || 'last_week'))}`);

    return normalizeChartingCollection(payload, entry);
}

export async function fetchBestSectorsStocks(entry = {}, period = '') {
    const payload = await get(`/api/v1/stock-finder/best-sectors/${encodeURIComponent(String(period || 'last_week'))}`);

    return normalizeChartingCollection(payload, entry);
}

export async function fetchHotValueStocks(entry = {}, period = '') {
    const payload = await get(`/api/v1/stock-finder/hot-values/${encodeURIComponent(String(period || 'last_week'))}`);

    return normalizeChartingCollection(payload, entry);
}

export async function fetchExchangeStocks(entry = {}, exchange = '') {
    const payload = await get(`/api/v1/stock-finder/stocks/index/${encodeURIComponent(String(exchange || ''))}`);

    return normalizeChartingCollection(payload, entry);
}

export async function fetchSectorStocks(entry = {}, sector = '') {
    const payload = await get('/api/v1/stock-finder/stocks/sector', {
        params: {
            sector
        }
    });

    return normalizeChartingCollection(payload, entry);
}

export async function fetchPlatinumStocks(entry = {}) {
    const payload = await get('/api/v1/stock-finder/stocks/platinum');

    return normalizeChartingCollection(payload, entry);
}

export async function fetchStocksByTicker(entry = {}, ticker = '') {
    const normalizedTicker = String(ticker || '')
        .trim()
        .toUpperCase();
    const payload = await get(`/api/v1/stock-finder/stocks/symbol/${encodeURIComponent(normalizedTicker)}`);

    if (Array.isArray(payload) && payload.length) {
        return normalizeChartingCollection(payload, entry);
    }

    const fallback = await get(`/api/v1/stock-finder/stocks/symbol-without-entry/${encodeURIComponent(normalizedTicker)}`);

    return normalizeChartingCollection(fallback ? [fallback] : [], entry);
}

export async function fetchFavoriteFolderStocks(entry = {}, folderName = '') {
    const payload = await get(`/api/v1/stock-finder/stocks/favoritos/${encodeURIComponent(String(folderName || ''))}`);

    return normalizeChartingCollection(payload, entry);
}

export async function fetchStocksFromArray(tickers = [], entry = {}) {
    const payload = await postForm('/api/v1/stock-finder/stocks/symbols/array', {
        array_tickers: Array.isArray(tickers) ? tickers.join(',') : String(tickers || '')
    });

    return normalizeChartingCollection(payload, entry);
}

export async function fetchTwinSoulStocks(entry = {}, twinIndex = '') {
    const payload = await get('/api/v1/widgets/get-twin-souls');
    const collection = Array.isArray(payload) ? payload : [];
    const item = collection[Number.parseInt(twinIndex, 10)] || null;
    const tickers = Array.isArray(item?.stocks) ? item.stocks.map((stock) => stock?.name).filter(Boolean) : [];

    return fetchStocksFromArray(tickers, entry);
}

export async function fetchReportStocks(entry = {}, reportSlug = '') {
    const payload = await get(`/api/v1/stock-finder/informe/${encodeURIComponent(String(reportSlug || ''))}`);

    return normalizeChartingCollection(payload, entry);
}

export async function searchChartingStocksByDescription(entry = {}, term = '') {
    const payload = await postForm('/api/v1/stock-finder/stocks/busqueda-descripcion', buildChartingDescriptionSearchPayload(term));

    return normalizeChartingCollection(payload, entry);
}

const COLLECTION_ENTRY_HANDLERS = {
    finder: (entry) => fetchDefaultStocks(entry),
    home: (entry) => fetchDefaultStocks(entry),
    compatibility: (entry, options) =>
        fetchCompatibilityStocks(entry, {
            currency: options?.currency || entry?.query?.currency || ''
        }),
    platinum: (entry) => fetchPlatinumStocks(entry),
    'last-ipos': (entry) => fetchLastIpoStocks(entry),
    'best-bullish-values': (entry) => fetchBestBullishStocks(entry, entry.value || 'last_week'),
    'best-sectors': (entry) => fetchBestSectorsStocks(entry, entry.value || 'last_week'),
    'hot-values': (entry) => fetchHotValueStocks(entry, entry.value || 'last_week'),
    exchange: (entry) => fetchExchangeStocks(entry, entry.value),
    sector: (entry) => fetchSectorStocks(entry, entry.value),
    ticker: (entry) => fetchStocksByTicker(entry, entry.value),
    favorites: (entry) => fetchFavoriteFolderStocks(entry, entry.value),
    'twin-souls': (entry) => fetchTwinSoulStocks(entry, entry.value || '0'),
    informe: (entry) => fetchReportStocks(entry, entry.value),
    'saved-search': (entry) => fetchNamedFinderStocks(entry, entry.value),
    description: (entry) => searchChartingStocksByDescription(entry, entry.value)
};

export async function fetchChartingCollectionByEntry(entry = {}, options = {}) {
    const entryType = String(entry?.type || 'finder').trim() || 'finder';
    const handler = COLLECTION_ENTRY_HANDLERS[entryType] || COLLECTION_ENTRY_HANDLERS.finder;

    return handler(entry, options);
}
